import { useQuery } from '@tanstack/react-query';
import { BookOpen, GraduationCap, Lightbulb, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ListRow,
  Spinner,
  Stat,
  StatusPill,
  oplStatusTone,
} from '../../components/ui';
import { api } from '../../lib/api';
import { humanise, relativeTime } from '../../lib/format';

interface CoachData {
  operatorCount: number;
  amSteps: Array<{
    stepNo: number;
    name: string;
    inPilot: boolean;
    operatorsIndependent: number;
    operatorsStarted: number;
    operatorTotal: number;
  }>;
  opls: {
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    pendingApproval: Array<{
      id: string;
      oplNo: string;
      title: string;
      oplType: string;
      status: string;
      updatedAt: string;
    }>;
  };
  training: Record<string, number>;
  recurringWithoutOpl: Array<{
    machineCode: string;
    componentName: string | null;
    occurrences: number;
  }>;
}

/** Answers the coach's question: are operators learning? (directive §79) */
export function CoachDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'coach'],
    queryFn: () => api.get<CoachData>('/api/dashboard/coach'),
  });

  if (isLoading || !data) return <Spinner label="Loading dashboard" />;

  const published = data.opls.byStatus.PUBLISHED ?? 0;
  const pilotSteps = data.amSteps.filter((s) => s.inPilot);
  const fullyCertified = pilotSteps.every((s) => s.operatorsIndependent > 0);

  return (
    <div>
      <PageHeader
        title="AM Coach"
        subtitle="Operator capability and the One-Point Lesson library"
        actions={
          <Button variant="secondary" icon={Users} onClick={() => navigate('/skill-matrix')}>
            Skill matrix
          </Button>
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Pilot operators" value={data.operatorCount} icon={Users} />
          <Stat
            label="OPLs published"
            value={published}
            hint="Proposal target: 10–15 in the pilot"
            tone={published >= 10 ? 'ok' : published > 0 ? 'warn' : 'neutral'}
            icon={BookOpen}
          />
          <Stat
            label="Waiting for approval"
            value={data.opls.pendingApproval.length}
            tone={data.opls.pendingApproval.length > 0 ? 'warn' : 'ok'}
          />
          <Stat
            label="Operators competent"
            value={data.training.COMPETENT ?? 0}
            hint="Across all lessons"
            tone="ok"
            icon={GraduationCap}
          />
        </div>

        <Card>
          <CardHeader
            title="AM step progress"
            subtitle="Operators assessed as independent or above, per step"
          />
          <div className="space-y-3 p-4">
            {data.amSteps.map((step) => (
              <div key={step.stepNo} className={step.inPilot ? '' : 'opacity-50'}>
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold text-steel-800">
                    Step {step.stepNo} — {step.name}
                    {!step.inPilot ? (
                      <Badge className="ml-2">Later phase</Badge>
                    ) : null}
                  </span>
                  <span className="text-xs font-bold tabular-nums text-steel-600">
                    {step.operatorsIndependent} / {step.operatorTotal}
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-steel-200">
                  <div
                    className={step.inPilot ? 'h-full bg-ok-500' : 'h-full bg-steel-300'}
                    style={{
                      width: `${step.operatorTotal === 0 ? 0 : (step.operatorsIndependent / step.operatorTotal) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          {!fullyCertified ? (
            <p className="border-t border-steel-200 px-4 py-3 text-xs text-steel-500">
              The pilot targets 2 operators certified on Steps 0–3 by Week 12.
            </p>
          ) : null}
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="OPLs waiting for you"
              action={
                <Link to="/opl" className="text-xs font-bold text-info-700 hover:underline">
                  All lessons
                </Link>
              }
            />
            {data.opls.pendingApproval.length === 0 ? (
              <EmptyState
                icon={BookOpen}
                title="Nothing waiting"
                message="No One-Point Lesson is waiting for review or approval."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.opls.pendingApproval.map((opl) => (
                  <li key={opl.id}>
                    <ListRow onClick={() => navigate(`/opl/${opl.id}`)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-steel-900">
                            <span className="font-mono text-info-700">{opl.oplNo}</span>{' '}
                            {opl.title}
                          </p>
                          <p className="text-xs text-steel-500">
                            {humanise(opl.oplType)} · updated {relativeTime(opl.updatedAt)}
                          </p>
                        </div>
                        <StatusPill tone={oplStatusTone(opl.status)} label={humanise(opl.status)} />
                      </div>
                    </ListRow>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Lessons waiting to be written"
              subtitle="Problems that keep coming back with no OPL yet"
            />
            {data.recurringWithoutOpl.length === 0 ? (
              <EmptyState
                icon={Lightbulb}
                title="Nothing recurring"
                message="No repeat problem is missing a lesson. Every recurring issue has been captured."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.recurringWithoutOpl.map((issue, index) => (
                  <li
                    key={`${issue.machineCode}-${index}`}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-steel-900">
                        {issue.machineCode} · {issue.componentName ?? 'Machine level'}
                      </p>
                      <p className="text-xs text-steel-500">
                        Tagged {issue.occurrences} times in 90 days — a good OPL candidate
                      </p>
                    </div>
                    <Badge tone="warn">{issue.occurrences}×</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card>
          <CardHeader title="Lesson library by type" />
          <div className="grid gap-3 p-4 sm:grid-cols-3">
            {(
              [
                ['BASIC_KNOWLEDGE', 'Basic Knowledge', 'How to do a task correctly'],
                ['PROBLEM_CASE', 'Problem Case', 'A real problem and how it was solved'],
                ['IMPROVEMENT', 'Improvement', 'An idea worth copying elsewhere'],
              ] as const
            ).map(([key, label, description]) => (
              <div key={key} className="rounded-lg border border-steel-200 p-3">
                <p className="text-2xl font-bold text-steel-900 tabular-nums">
                  {data.opls.byType[key] ?? 0}
                </p>
                <p className="text-sm font-semibold text-steel-800">{label}</p>
                <p className="mt-0.5 text-xs text-steel-500">{description}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
